import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, Music, Download, CheckCircle, Clock,
  ThumbsUp, FileText, ChevronDown, Upload, ImageIcon, X, Mail, RefreshCw,
  Play, Pause, Repeat
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { Tables } from "@/integrations/supabase/types";

type SongOrder = Tables<"song_orders">;

const statusConfig: Record<string, { label: string; color: string; description: string }> = {
  submitted: { label: "Submitted", color: "bg-muted text-muted-foreground", description: "Your order has been received." },
  in_review: { label: "In Review", color: "bg-primary/10 text-primary", description: "Our team is reviewing your details." },
  drafting_lyrics: { label: "Writing Lyrics", color: "bg-primary/10 text-primary", description: "Your song lyrics are being written." },
  composing: { label: "Composing", color: "bg-primary/10 text-primary", description: "Your song is being composed and produced." },
  ready_for_review: { label: "Ready for Review", color: "bg-accent/20 text-accent-foreground", description: "Your song is ready! Please review and approve." },
  revision_requested: { label: "Changes Requested", color: "bg-orange-100 text-orange-700", description: "We're working on your requested changes." },
  approved: { label: "Approved", color: "bg-green-100 text-green-700", description: "You've approved your song. Finalizing delivery." },
  completed: { label: "Completed", color: "bg-green-100 text-green-700", description: "Your song is complete and ready to download!" },
};

const eventTypeLabels: Record<string, string> = {
  wedding: "Wedding", birthday: "Birthday", anniversary: "Anniversary",
  graduation: "Graduation", memorial: "Memorial", proposal: "Proposal",
  baby_shower: "Baby Shower", retirement: "Retirement", other: "Other",
};

const customerPhotosPathPrefixes = [
  "/storage/v1/object/sign/customer-photos/",
  "/storage/v1/object/authenticated/customer-photos/",
  "/storage/v1/object/private/customer-photos/",
  "/storage/v1/object/public/customer-photos/",
  "/object/sign/customer-photos/",
  "/object/authenticated/customer-photos/",
  "/object/private/customer-photos/",
  "/object/public/customer-photos/",
];

const normalizeCustomerPhotosPath = (value: string): string | null => {
  let filePath = value.trim();

  for (const prefix of customerPhotosPathPrefixes) {
    if (filePath.includes(prefix)) {
      filePath = filePath.split(prefix)[1]?.split("?")[0]?.split("#")[0] || "";
      break;
    }
  }

  filePath = filePath.split("?")[0]?.split("#")[0] || filePath;
  if (filePath.startsWith("/")) filePath = filePath.slice(1);
  if (filePath.startsWith("customer-photos/")) {
    filePath = filePath.slice("customer-photos/".length);
  }

  if (!filePath || filePath.startsWith("http")) return null;

  try {
    filePath = decodeURIComponent(filePath);
  } catch {
    // keep original filePath if decode fails
  }

  return filePath || null;
};

const exportSurvey = (order: SongOrder) => {
  const lines = [
    `ORDER #${order.order_number}`,
    `Plan: ${order.plan_type}`,
    `Status: ${order.status}`,
    `Created: ${new Date(order.created_at).toLocaleString()}`,
    `Rush Delivery: ${order.rush_delivery ? "Yes" : "No"}`,
    "",
    "--- SURVEY DETAILS ---",
    `Occasion: ${eventTypeLabels[order.event_type] || order.event_type}`,
    `Event Date: ${order.event_date ? new Date(order.event_date).toLocaleDateString() : "N/A"}`,
    `Recipient Names: ${order.recipient_names}`,
    `Relationship: ${order.relationship_to_recipient || "N/A"}`,
    `Language: ${order.language || "English"}`,
    `Singer Gender: ${order.singer_gender || "N/A"}`,
    `Musical Style: ${order.musical_style}`,
    `Mood/Feeling: ${order.mood}`,
    `Tempo: ${order.tempo || "Medium"}`,
    "",
    "--- STORY & MEMORIES ---",
    order.memories,
    "",
    ...(order.specific_phrases ? ["--- SPECIFIC PHRASES ---", order.specific_phrases, ""] : []),
    ...(order.additional_notes ? ["--- ADDITIONAL NOTES ---", order.additional_notes, ""] : []),
    ...(order.pronunciation_url ? [`Pronunciation Recording: ${order.pronunciation_url}`] : []),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `order-${order.order_number}-survey.txt`;
  a.click();
  URL.revokeObjectURL(url);
};

const OrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [order, setOrder] = useState<SongOrder | null>(null);
  const [allOrders, setAllOrders] = useState<SongOrder[]>([]);
  const [images, setImages] = useState<Tables<"image_uploads">[]>([]);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [deliverableUrls, setDeliverableUrls] = useState<Record<string, string>>({});
  const [pronunciationUrl, setPronunciationUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [trackerOpen, setTrackerOpen] = useState(false);
  const [videoRetrying, setVideoRetrying] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoLoop, setVideoLoop] = useState(false);
  const songAudioRef = useRef<HTMLAudioElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoErrorRetried = useRef(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [user, authLoading, navigate]);

  // Fetch all user orders for quick nav
  useEffect(() => {
    const fetchAllOrders = async () => {
      if (!user) return;
      const { data } = await supabase.from("song_orders").select("*").neq("status", "pending_payment").order("created_at", { ascending: false });
      if (data) setAllOrders(data);
    };
    if (user) fetchAllOrders();
  }, [user]);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id || !user) return;
      setLoading(true);
      setPronunciationUrl(null);
      videoErrorRetried.current = false;
      const [orderRes, imgRes] = await Promise.all([
        supabase.from("song_orders").select("*").eq("id", id).neq("status", "pending_payment").maybeSingle(),
        supabase.from("image_uploads").select("*").eq("order_id", id),
      ]);
      if (orderRes.data) {
        setOrder(orderRes.data);
        const urls: Record<string, string> = {};
        const fieldsToSign = [
          { key: "pronunciation_url", value: orderRes.data.pronunciation_url },
          { key: "draft_audio_url", value: orderRes.data.draft_audio_url },
          { key: "final_audio_url", value: orderRes.data.final_audio_url },
          { key: "draft_video_url", value: orderRes.data.draft_video_url },
          { key: "final_video_url", value: orderRes.data.final_video_url },
          { key: "lyrics_pdf_url", value: orderRes.data.lyrics_pdf_url },
        ];
        for (const field of fieldsToSign) {
          if (!field.value) continue;

          const normalizedPath = normalizeCustomerPhotosPath(field.value);
          if (normalizedPath) {
            const { data: signedData, error: signError } = await supabase.storage
              .from("customer-photos")
              .createSignedUrl(normalizedPath, 3600);
            if (signError) {
              console.error(`Failed to generate signed URL for ${field.key} (path: ${normalizedPath}):`, signError.message);
            }
            if (signedData?.signedUrl) urls[field.key] = signedData.signedUrl;
            continue;
          }

          if (field.value.startsWith("http")) {
            urls[field.key] = field.value;
          }
        }
        setDeliverableUrls(urls);
        if (urls.pronunciation_url) setPronunciationUrl(urls.pronunciation_url);
      }
      if (imgRes.data) {
        setImages(imgRes.data);
        const urls: Record<string, string> = {};
        for (const img of imgRes.data) {
          const { data: signed } = await supabase.storage
            .from("customer-photos")
            .createSignedUrl(img.file_path, 3600);
          if (signed?.signedUrl) urls[img.id] = signed.signedUrl;
        }
        setImageUrls(urls);
      }
      setLoading(false);
    };
    if (user) fetchOrder();
  }, [id, user]);

  const bothApproved = order?.song_approved && order?.video_approved;

  const handleSongPlay = () => {
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
  };

  const handleVideoPlay = () => {
    if (songAudioRef.current && !songAudioRef.current.paused) {
      songAudioRef.current.pause();
    }
  };

  const refreshSignedDeliverableUrl = async (key: string, rawValue: string | null | undefined) => {
    if (!rawValue) return;
    const normalizedPath = normalizeCustomerPhotosPath(rawValue);
    if (!normalizedPath) return;

    const { data: signedData, error } = await supabase.storage
      .from("customer-photos")
      .createSignedUrl(normalizedPath, 3600);

    if (error) {
      console.error(`Failed to refresh signed URL for ${key}:`, error.message);
      return;
    }

    if (signedData?.signedUrl) {
      setDeliverableUrls((prev) => ({ ...prev, [key]: signedData.signedUrl }));
    }
  };

  const handleApproveSong = async () => {
    if (!order || !user) return;
    setSubmitting(true);
    const updateData: Record<string, any> = { song_approved: true };
    if (order.video_approved || order.plan_type === "essentials") {
      updateData.approved = true;
      updateData.status = "approved";
    }
    const { error } = await supabase.from("song_orders").update(updateData).eq("id", order.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      const updated = { ...order, ...updateData };
      setOrder(updated as any);
      toast({ title: "Song approved!", description: updated.approved ? "All deliverables approved!" : "Now review the video." });
    }
    setSubmitting(false);
  };

  const handleApproveVideo = async () => {
    if (!order || !user) return;
    setSubmitting(true);
    const updateData: Record<string, any> = { video_approved: true };
    if (order.song_approved) {
      updateData.approved = true;
      updateData.status = "approved";
    }
    const { error } = await supabase.from("song_orders").update(updateData).eq("id", order.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      const updated = { ...order, ...updateData };
      setOrder(updated as any);
      toast({ title: "Video approved!", description: updated.approved ? "All deliverables approved!" : "Now review the song." });
    }
    setSubmitting(false);
  };


  const handlePhotoUpload = async () => {
    if (!order || !user) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/heic";
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length === 0) return;
      setUploadingPhotos(true);

      for (const file of files) {
        if (file.size > 20 * 1024 * 1024) {
          toast({ title: "File too large", description: `${file.name} exceeds 20MB limit.`, variant: "destructive" });
          continue;
        }

        const path = `${user.id}/${order.id}/${Date.now()}-${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from("customer-photos")
          .upload(path, file);

        if (uploadError) {
          toast({ title: "Upload failed", description: uploadError.message, variant: "destructive" });
          continue;
        }

        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        await new Promise<void>((resolve) => { img.onload = () => resolve(); img.src = objectUrl; });
        URL.revokeObjectURL(objectUrl);

        const { data: insertData, error: dbError } = await supabase.from("image_uploads").insert({
          file_name: file.name,
          file_path: path,
          file_size: file.size,
          mime_type: file.type,
          width: img.width,
          height: img.height,
          upload_session_id: order.id,
          user_id: user.id,
          order_id: order.id,
          status: "pending",
        }).select().single();

        if (!dbError && insertData) {
          setImages((prev) => [...prev, insertData]);
          const { data: signed } = await supabase.storage
            .from("customer-photos")
            .createSignedUrl(path, 3600);
          if (signed?.signedUrl) {
            setImageUrls((prev) => ({ ...prev, [insertData.id]: signed.signedUrl }));
          }
        }
      }

      setUploadingPhotos(false);
      toast({ title: "Photos uploaded", description: `${files.length} photo(s) added.` });
    };
    input.click();
  };

  const handlePhotoDelete = async (img: Tables<"image_uploads">) => {
    if (!order || !user) return;
    const { error: storageErr } = await supabase.storage.from("customer-photos").remove([img.file_path]);
    if (storageErr) {
      toast({ title: "Delete failed", description: storageErr.message, variant: "destructive" });
      return;
    }
    const { error: dbErr } = await supabase.from("image_uploads").delete().eq("id", img.id);
    if (dbErr) {
      toast({ title: "Delete failed", description: dbErr.message, variant: "destructive" });
      return;
    }
    setImages(prev => prev.filter(i => i.id !== img.id));
    setImageUrls(prev => { const copy = { ...prev }; delete copy[img.id]; return copy; });
    toast({ title: "Photo removed" });
  };

  const handleDownload = async (rawPath: string) => {
    try {
      let storagePath = rawPath;
      const publicPrefix = "/object/public/customer-photos/";
      if (storagePath.includes(publicPrefix)) {
        storagePath = storagePath.split(publicPrefix)[1];
      }
      const { data, error } = await supabase.storage
        .from("customer-photos")
        .download(storagePath);
      if (error) throw error;
      const blobUrl = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = storagePath.split("/").pop() || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast({ title: "Download failed", description: "Please try again.", variant: "destructive" });
    }
    if (order) {
      const updates: Record<string, any> = {};
      if (order.approved && order.status !== "completed") {
        updates.status = "completed";
      }
      if (!order.song_downloaded) {
        updates.song_downloaded = true;
        updates.song_downloaded_at = new Date().toISOString();
      }
      if (Object.keys(updates).length > 0) {
        const { error } = await supabase.from("song_orders")
          .update(updates)
          .eq("id", order.id);
        if (!error) {
          setOrder({ ...order, ...updates });
        }
      }

      // Clean up customer-uploaded images after video download to free storage
      const isVideoDownload =
        rawPath === order.final_video_url || rawPath === order.draft_video_url;
      if (isVideoDownload && images.length > 0) {
        try {
          const storagePaths = images.map((img) => img.file_path);
          await supabase.storage.from("customer-photos").remove(storagePaths);
          const imageIds = images.map((img) => img.id);
          await supabase.from("image_uploads").delete().in("id", imageIds);
          setImages([]);
          setImageUrls({});
        } catch (err) {
          console.error("Failed to clean up uploaded images:", err);
        }
      }
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-16 container mx-auto px-4 text-center">
          <p className="text-muted-foreground">Order not found.</p>
          <Button variant="outline" className="mt-4" asChild><Link to="/dashboard">Back to Dashboard</Link></Button>
        </main>
        <Footer />
      </div>
    );
  }

  const status = statusConfig[order.status] || statusConfig.submitted;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" asChild>
              <Link to="/dashboard"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard</Link>
            </Button>
            {allOrders.length > 1 && (
              <p className="text-xs text-muted-foreground">{allOrders.length} orders</p>
            )}
          </div>

          {/* Quick Order Nav */}
          {allOrders.length > 1 && (
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {allOrders.map((o) => (
                <Link
                  key={o.id}
                  to={`/order/${o.id}`}
                  className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${
                    o.id === id
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                  }`}
                >
                  #{o.order_number} · {eventTypeLabels[o.event_type] || o.event_type}
                </Link>
              ))}
            </div>
          )}

          <div className="space-y-6">
            {/* Order Progress Tracker - Collapsible */}
            {(() => {
              const steps = [
                { key: "submitted", label: "Order Submitted" },
                { key: "in_review", label: "In Review" },
                { key: "drafting_lyrics", label: "Writing Lyrics" },
                { key: "composing", label: "Composing" },
                { key: "ready_for_review", label: "Ready for Review" },
                { key: "approved", label: "Approved" },
                { key: "completed", label: "Completed" },
              ];
              const statusOrder = ["submitted", "in_review", "drafting_lyrics", "composing", "ready_for_review", "approved", "completed"];
              const currentIdx = statusOrder.indexOf(order.status);


              return (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-card rounded-2xl shadow-soft overflow-hidden">
                  <button
                    onClick={() => setTrackerOpen(!trackerOpen)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Music className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-semibold text-foreground">
                          {status.label}
                        </h3>
                        <p className="text-sm text-muted-foreground">{status.description}</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${trackerOpen ? "rotate-180" : ""}`} />
                  </button>

                  {trackerOpen && (
                    <div className="px-6 pb-6 pt-0 border-t border-border">
                      <div className="mt-4 space-y-0">
                        {steps.map((step, i) => {
                          const stepIdx = statusOrder.indexOf(step.key);
                          const isActive = step.key === order.status;
                          const isComplete = stepIdx < currentIdx && !isActive;
                          const isLast = i === steps.length - 1;

                          return (
                            <div key={step.key} className="flex items-start gap-3">
                              <div className="flex flex-col items-center">
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                                  isComplete
                                    ? "bg-primary border-primary text-primary-foreground"
                                    : isActive
                                      ? "border-primary bg-primary/10 text-primary"
                                      : "border-border bg-muted text-muted-foreground"
                                }`}>
                                  {isComplete ? <CheckCircle className="w-4 h-4" /> : i + 1}
                                </div>
                                {!isLast && (
                                  <div className={`w-0.5 h-6 ${isComplete ? "bg-primary" : "bg-border"}`} />
                                )}
                              </div>
                              <div className="pt-1">
                                <p className={`text-sm font-medium ${
                                  isActive ? "text-primary" : isComplete ? "text-foreground" : "text-muted-foreground"
                                }`}>
                                  {step.label}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-muted-foreground mt-4">You'll receive an email notification when your order status changes.</p>
                    </div>
                  )}
                </motion.div>
              );
            })()}

            {/* Order Info */}
            <div className="bg-card rounded-2xl shadow-soft p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-semibold text-foreground">Order Details</h3>
                <div className="flex items-center gap-3">
                  {order.order_number && (
                    <span className="text-sm text-muted-foreground font-medium">Order #{order.order_number}</span>
                  )}
                  <Button variant="outline" size="sm" onClick={() => exportSurvey(order)}>
                    <FileText className="w-4 h-4 mr-2" /> Export
                  </Button>
                </div>
              </div>
              {user?.email && (
                <div className="mb-4 pb-4 border-b border-border">
                  <p className="text-muted-foreground text-xs uppercase font-semibold">Account Email</p>
                  <p className="text-foreground text-sm">{user.email}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-muted-foreground text-xs uppercase font-semibold">Plan</p><p className="text-foreground">{order.plan_type?.charAt(0).toUpperCase() + order.plan_type?.slice(1)}</p></div>
                <div><p className="text-muted-foreground text-xs uppercase font-semibold">Occasion</p><p className="text-foreground">{eventTypeLabels[order.event_type] || order.event_type}</p></div>
                <div><p className="text-muted-foreground text-xs uppercase font-semibold">Event Date</p><p className="text-foreground">{order.event_date ? new Date(order.event_date).toLocaleDateString() : "—"}</p></div>
                <div><p className="text-muted-foreground text-xs uppercase font-semibold">Recipient</p><p className="text-foreground">{order.recipient_names}</p></div>
                <div><p className="text-muted-foreground text-xs uppercase font-semibold">Relationship</p><p className="text-foreground">{order.relationship_to_recipient || "—"}</p></div>
                <div><p className="text-muted-foreground text-xs uppercase font-semibold">Language</p><p className="text-foreground capitalize">{order.language || "English"}</p></div>
                <div><p className="text-muted-foreground text-xs uppercase font-semibold">Style</p><p className="text-foreground capitalize">{order.musical_style}</p></div>
                <div><p className="text-muted-foreground text-xs uppercase font-semibold">Mood</p><p className="text-foreground">{order.mood}</p></div>
                <div><p className="text-muted-foreground text-xs uppercase font-semibold">Tempo</p><p className="text-foreground capitalize">{order.tempo || "Medium"}</p></div>
                {order.rush_delivery && (
                  <div><p className="text-muted-foreground text-xs uppercase font-semibold">Rush Delivery</p><p className="text-foreground">Yes</p></div>
                )}
              </div>
            </div>

            {/* Story & Memories */}
            <div className="bg-card rounded-2xl shadow-soft p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Your Story</h3>
              <p className="text-sm text-foreground bg-muted/50 rounded-lg p-3">{order.memories}</p>
              {order.specific_phrases && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Specific Phrases</p>
                  <p className="text-sm text-foreground bg-muted/50 rounded-lg p-3">{order.specific_phrases}</p>
                </div>
              )}
              {order.additional_notes && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Additional Notes</p>
                  <p className="text-sm text-foreground bg-muted/50 rounded-lg p-3">{order.additional_notes}</p>
                </div>
              )}
              {pronunciationUrl && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Name Pronunciation</p>
                  <audio controls src={pronunciationUrl} className="w-full mt-1" />
                </div>
              )}
            </div>

            {/* Photos Section - Premium Only */}
            {(order.plan_type === "premium" || order.plan_type === "premium_plus") && (
              <div className="bg-card rounded-2xl shadow-soft p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    📸 Your Photos ({images.length}/40)
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePhotoUpload}
                    disabled={uploadingPhotos || images.length >= 40}
                  >
                    {uploadingPhotos ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload Photos
                  </Button>
                </div>

                {/* Image Requirements */}
                <div className="bg-muted/50 rounded-lg p-3 mb-4 text-sm text-muted-foreground space-y-1">
                  <p className="font-semibold text-foreground text-xs uppercase tracking-wide">Image Requirements</p>
                  <ul className="list-disc list-inside space-y-0.5 text-xs">
                    <li>Minimum 1500 × 1500 pixels</li>
                    <li>JPG, PNG, or HEIC format</li>
                    <li>15–40 photos recommended for your video slideshow</li>
                    <li>Max 20MB per image</li>
                    <li>Note: Not all photos may be used depending on the final video</li>
                  </ul>
                </div>

                {images.length > 0 ? (
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                    {images.map((img) => (
                      <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden bg-muted group">
                        {imageUrls[img.id] ? (
                          <img src={imageUrls[img.id]} alt={img.file_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                          </div>
                        )}
                        <button
                          onClick={() => handlePhotoDelete(img)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-destructive/90 text-destructive-foreground flex items-center justify-center hover:bg-destructive transition-colors"
                          aria-label="Remove photo"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        {img.width < 1500 || img.height < 1500 ? (
                          <div className="absolute bottom-0 left-0 right-0 bg-destructive/90 text-destructive-foreground text-[10px] text-center py-0.5">
                            Low res ({img.width}×{img.height})
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : order.song_downloaded ? (
                  <div className="border border-primary/20 bg-primary/5 rounded-xl p-6 text-center">
                    <CheckCircle className="w-10 h-10 text-primary mx-auto mb-3" />
                    <p className="text-sm font-medium text-foreground mb-1">Your photos have been removed</p>
                    <p className="text-xs text-muted-foreground">
                      For your privacy, all uploaded photos were automatically deleted after your video was downloaded. They are no longer stored on our servers.
                    </p>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-border rounded-xl p-8 text-center">
                    <ImageIcon className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-3">No photos uploaded yet.</p>
                    <Button variant="outline" size="sm" onClick={handlePhotoUpload} disabled={uploadingPhotos}>
                      {uploadingPhotos ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload Photos
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Your Deliverables + Actions */}
            <div className="bg-card rounded-2xl shadow-soft p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Your Deliverables</h3>

              {/* Song Section */}
              <div className="border border-border rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-foreground">🎵 Song (MP3)</p>
                </div>
                {(order.final_audio_url || order.draft_audio_url) ? (
                  deliverableUrls.final_audio_url || deliverableUrls.draft_audio_url ? (
                    <audio
                      ref={songAudioRef}
                      controls
                      controlsList="nodownload"
                      src={deliverableUrls.final_audio_url || deliverableUrls.draft_audio_url}
                      className="w-full"
                      onPlay={handleSongPlay}
                    />
                  ) : loading ? (
                    <p className="text-sm text-muted-foreground italic">Loading audio…</p>
                  ) : (
                    <p className="text-sm text-destructive italic">Unable to load audio. The file may not be available yet. Please contact support if this persists.</p>
                  )
                ) : (
                  <p className="text-sm text-muted-foreground italic">Not yet available</p>
                )}

                {/* Song Approval Actions */}
                {(order.final_audio_url || order.draft_audio_url) && !order.song_approved && (
                  <div className="mt-3 pt-3 border-t border-border space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" onClick={handleApproveSong} disabled={submitting}>
                        <ThumbsUp className="w-3.5 h-3.5 mr-1.5" /> Approve Song
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Approving releases the song for download.</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Not quite right?{" "}
                      <Link to={`/contact?order=${order.order_number || ""}&type=song`} className="text-primary hover:underline">Email us</Link>
                    </p>
                  </div>
                )}
                {order.song_approved && (
                  <div className="mt-3 pt-3 border-t border-border flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">Song Approved</span>
                  </div>
                )}
              </div>

              {/* Video Section - show when video URL exists or plan includes video */}
              {(order.plan_type !== "essentials" || order.final_video_url || order.draft_video_url) && (
                <div className="border border-border rounded-xl p-4 mb-4">
                  <p className="text-sm font-medium text-foreground mb-2">🎬 Video (MP4)</p>
                  {(order.final_video_url || order.draft_video_url) ? (
                    deliverableUrls.final_video_url || deliverableUrls.draft_video_url ? (
                      <>
                        <div className="relative rounded-lg overflow-hidden bg-black">
                          <video
                            ref={videoRef}
                            controlsList="nodownload"
                            preload="auto"
                            playsInline
                            loop={videoLoop}
                            className="w-full rounded-lg"
                            onPlay={() => {
                              handleVideoPlay();
                              setVideoPlaying(true);
                            }}
                            onPause={() => setVideoPlaying(false)}
                            onEnded={() => setVideoPlaying(false)}
                            onTimeUpdate={() => {
                              if (videoRef.current && videoRef.current.duration) {
                                setVideoProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
                              }
                            }}
                            onError={() => {
                              if (videoErrorRetried.current) return;
                              videoErrorRetried.current = true;
                              const rawVideoValue = order.final_video_url || order.draft_video_url;
                              const key = order.final_video_url ? "final_video_url" : "draft_video_url";
                              void refreshSignedDeliverableUrl(key, rawVideoValue);
                            }}
                            src={deliverableUrls.final_video_url || deliverableUrls.draft_video_url}
                          >
                            Your browser does not support video playback.
                          </video>
                        </div>

                        {/* Custom controls: progress bar + play/pause + repeat + refresh */}
                        <div className="mt-2 space-y-2">
                          <div
                            className="cursor-pointer"
                            onClick={(e) => {
                              if (!videoRef.current || !videoRef.current.duration) return;
                              const rect = e.currentTarget.getBoundingClientRect();
                              const pct = (e.clientX - rect.left) / rect.width;
                              videoRef.current.currentTime = pct * videoRef.current.duration;
                            }}
                          >
                            <Progress value={videoProgress} className="h-2 cursor-pointer" />
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary/20 transition-colors"
                              onClick={() => {
                                if (!videoRef.current) return;
                                if (videoRef.current.paused) {
                                  videoRef.current.play();
                                } else {
                                  videoRef.current.pause();
                                }
                              }}
                              aria-label={videoPlaying ? "Pause video" : "Play video"}
                            >
                              {videoPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto"
                              disabled={videoRetrying}
                              onClick={async () => {
                                setVideoRetrying(true);
                                videoErrorRetried.current = false;
                                const rawVideoValue = order.final_video_url || order.draft_video_url;
                                const key = order.final_video_url ? "final_video_url" : "draft_video_url";
                                await refreshSignedDeliverableUrl(key, rawVideoValue);
                                if (videoRef.current) videoRef.current.load();
                                setVideoRetrying(false);
                              }}
                            >
                              {videoRetrying ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                              {videoRetrying ? "Refreshing…" : "Refresh video"}
                            </button>
                          </div>
                        </div>
                      </>
                    ) : loading ? (
                      <p className="text-sm text-muted-foreground italic">Loading video…</p>
                    ) : (
                      <p className="text-sm text-destructive italic">Unable to load video. The file may not be available yet. Please contact support if this persists.</p>
                    )
                  ) : (
                    <p className="text-sm text-muted-foreground italic">Not yet available</p>
                  )}

                  {/* Video Approval Actions */}
                  {(order.final_video_url || order.draft_video_url) && !order.video_approved && (
                    <div className="mt-3 pt-3 border-t border-border space-y-2">
                      {!order.song_approved && (
                        <p className="text-xs text-amber-600 bg-amber-50 rounded-md px-3 py-2 font-medium">
                          ⚠️ You must approve the song before you can approve and download the video.
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={handleApproveVideo} disabled={submitting || !order.song_approved}>
                          <ThumbsUp className="w-3.5 h-3.5 mr-1.5" /> Approve Video
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">Approving releases the video for download.</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Mail className="w-3 h-3" /> Not quite right?{" "}
                        <Link to={`/contact?order=${order.order_number || ""}&type=video`} className="text-primary hover:underline">Email us</Link>
                      </p>
                    </div>
                  )}
                  {order.video_approved && (
                    <div className="mt-3 pt-3 border-t border-border flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Video Approved</span>
                    </div>
                  )}
                </div>
              )}

              {/* PDF */}
              {order.plan_type !== "essentials" && (
                <div className="border border-border rounded-xl p-4 mb-4">
                  <p className="text-sm font-medium text-foreground mb-2">📄 Lyric Sheet (PDF)</p>
                  {(order as any).lyrics_pdf_url ? (
                    <Button size="sm" variant="outline" onClick={() => handleDownload((order as any).lyrics_pdf_url)}>
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Download Lyric Sheet
                    </Button>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">Not yet available</p>
                  )}
                </div>
              )}

              {/* All Approved - Download Section */}
              {bothApproved && (
                <div className="mt-6 space-y-4">
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">All Deliverables Approved!</p>
                      <p className="text-xs text-muted-foreground">Download your files below.</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {order.final_audio_url && (
                      <Button onClick={() => handleDownload(order.final_audio_url!)}>
                        <Download className="w-4 h-4 mr-2" /> Download Audio
                      </Button>
                    )}
                    {order.plan_type !== "essentials" && order.final_video_url && (
                      <Button variant="outline" onClick={() => handleDownload(order.final_video_url!)}>
                        <Download className="w-4 h-4 mr-2" /> Download Video
                      </Button>
                    )}
                    {order.plan_type !== "essentials" && (order as any).lyrics_pdf_url && (
                      <Button variant="outline" onClick={() => handleDownload((order as any).lyrics_pdf_url)}>
                        <Download className="w-4 h-4 mr-2" /> Download Lyrics PDF
                      </Button>
                    )}
                  </div>
                  {order.status === "completed" && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3 text-primary" /> Delivery complete
                    </p>
                  )}
                </div>
              )}

              {/* Essentials plan - single approval complete */}
              {order.plan_type === "essentials" && order.song_approved && (
                <div className="mt-6 space-y-4">
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">Song Approved!</p>
                      <p className="text-xs text-muted-foreground">Download your file below.</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {order.final_audio_url && (
                      <Button onClick={() => handleDownload(order.final_audio_url!)}>
                        <Download className="w-4 h-4 mr-2" /> Download Audio
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OrderDetail;
